import React, { useState } from "react"

import { Loader2 } from "lucide-react"
import { Link, Navigate } from "react-router-dom"

import AuthCard, { StaffEmailNote } from "@/components/AuthCard"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { isStaffEmail, useAuth } from "@/contexts/AuthContext"

/** Staff use Google; partners use email and password. A HIVED address in the form is never sent. */
const Login: React.FC = () => {
  const { user, loading, signInWithPassword } = useAuth()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (loading) return null
  if (user) return <Navigate to="/collections/monitoring" replace />

  const staff = isStaffEmail(email)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (staff) return
    setSubmitting(true)
    setError(null)
    try {
      await signInWithPassword(email.trim(), password)
    } catch {
      // One message either way, so the form does not reveal which addresses have a login.
      setError("That email and password don't match a login. Check them and try again.")
      setSubmitting(false)
    }
  }

  return (
    <AuthCard title="Sign in">
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <div>
          <Label htmlFor="login-email">Email address</Label>
          <Input
            id="login-email"
            type="email"
            autoComplete="username"
            placeholder="name@host.com"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1"
          />
          {staff && <StaffEmailNote />}
        </div>
        <div>
          <Label htmlFor="login-password">Password</Label>
          <Input
            id="login-password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            placeholder="Enter password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1"
          />
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="login-show-password"
            checked={showPassword}
            onCheckedChange={(checked) => setShowPassword(checked === true)}
          />
          <Label htmlFor="login-show-password" className="font-normal">
            Show password
          </Label>
        </div>
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        <Button type="submit" className="w-full gap-2" disabled={submitting || staff}>
          {submitting && <Loader2 size={14} className="animate-spin" />}
          Sign in
        </Button>
      </form>

      <p className="mt-4 text-center text-sm text-gray-700">
        Don&rsquo;t have an account yet?{" "}
        <Link to="/signup" className="font-medium underline underline-offset-2">
          Sign up
        </Link>
      </p>
    </AuthCard>
  )
}

export default Login
